/**
 * The employee page's Tid & lønn tab (owner brief, 2026-09-21): the agreement as it is stored,
 * and the last four weeks of hours against it. Only settings that exist are listed (owner,
 * round 2). The chart is hours, not kroner: pay needs its own permission and route, and hours
 * answer "is this person working what was agreed". Rediger opens the editor on this part.
 */
import type { ApiShift, ApiUserFull } from '@/api/data';
import { T } from '@/i18n';
import { breakCalcLabel } from '@/rules/breakSetting';
import { Chip, Panel } from '@/ui';
import { fmtDs, num } from '@/ui/format';
import type { BreakMode } from '@timefly/core';
import { type BreakPolicy, addDays, isoWeek } from '@timefly/core';
import { AgreedMissing } from './Oversikt';
import { TimeBankPanel } from './TimeBank';
import { weekFigures } from './summary';

export function TidTab({
  u,
  mine,
  monday,
  policy,
  breakMode,
  seeLoenn,
  onEdit,
}: {
  u: ApiUserFull;
  mine: ApiShift[];
  monday: string;
  policy: BreakPolicy | null;
  breakMode: BreakMode;
  seeLoenn: boolean;
  /** opens Rediger ansatt on this part, or on a named field */
  onEdit: (field?: string) => void;
}) {
  const weeks = [-21, -14, -7, 0].map((d) => {
    const m = addDays(monday, d);
    return { m, ...weekFigures(mine, m, addDays(m, 6), policy, u.max_hours_per_week) };
  });
  const done = weeks.slice(0, 3);
  const avg = done.reduce((a, w) => a + w.worked, 0) / done.length;
  const top = Math.max(1, u.max_hours_per_week ?? 0, ...weeks.map((w) => Math.max(w.worked, w.planned)));
  const agreed = u.max_hours_per_week != null && u.max_hours_per_week > 0 ? u.max_hours_per_week : null;
  const edit = (
    <button
      type="button"
      className="btn sm"
      onClick={() => onEdit()}
      aria-label={T('Rediger arbeidstid og lønn')}
    >
      {T('Rediger')}
    </button>
  );
  return (
    <div className="cols2">
      <Panel item title={T('Arbeidstid og lønn')} actions={edit}>
        <dl className="ovdl">
          <dt>{T('Avtalt arbeidstid')}</dt>
          <dd>{agreed ? `${num(agreed)} t ${T('per uke')}` : <AgreedMissing u={u} onEdit={onEdit} />}</dd>
          <dt>{T('Pauser')}</dt>
          <dd>
            {breakCalcLabel(breakMode)}
            {breakMode !== 'punched' && policy ? `, ${policy.autoMinutes} min` : ''}
            {/* the source in brackets on the same line, not a stray clause that wraps alone (#272 B-16) */}
            <span className="ovmute"> ({T('bedriftens innstilling')})</span>
          </dd>
          {seeLoenn && (
            <>
              <dt>{T('Lønnstype')}</dt>
              <dd>{u.contract_type === 'monthly' ? T('Fastlønn') : T('Timelønn')}</dd>
              <dt>{u.contract_type === 'monthly' ? T('Månedslønn') : T('Timelønn')}</dt>
              <dd>
                {(() => {
                  const v = u.contract_type === 'monthly' ? u.monthly_rate : u.hourly_rate;
                  return v ? (
                    `${num(Number(v))} kr${u.contract_type === 'monthly' ? '' : ` ${T('per time')}`}`
                  ) : (
                    <Chip tone="dgr" small>
                      {T('Mangler')}
                    </Chip>
                  );
                })()}
              </dd>
            </>
          )}
          <dt>{T('Feriedager')}</dt>
          <dd>
            {u.vacation_days_balance ?? '–'} {T('igjen')}, {u.total_vacation_days_per_year ?? '–'}{' '}
            {T('per år')}
          </dd>
        </dl>
      </Panel>
      <Panel item title={T('Timer siste fire uker')}>
        <div className="twk">
          {weeks.map((w) => (
            <div key={w.m} className="twkc">
              <div className="twkbars" aria-hidden="true">
                <i className="p" style={{ height: `${(w.planned / top) * 100}%` }} />
                <i className="w" style={{ height: `${(w.worked / top) * 100}%` }} />
              </div>
              <b>{num(Math.round(w.worked * 10) / 10)} t</b>
              <small>
                {T('Uke {n}', { n: isoWeek(w.m) })}, {fmtDs(w.m)}
              </small>
              <small>{T('planlagt {n} t', { n: num(Math.round(w.planned * 10) / 10) })}</small>
            </div>
          ))}
        </div>
        <p className="hint">
          {T('Snitt de tre siste hele ukene: {n} t', { n: num(Math.round(avg * 10) / 10) })}
          {agreed ? ` ${T('av {n} t avtalt', { n: num(agreed) })}` : ''}.{' '}
          {T('Jobbede timer etter pause, fra stemplingene. Lys søyle er planlagt.')}
        </p>
      </Panel>
      {/* the overtime bank moves pay, so it shows with pay (plan 0027 G2) */}
      {seeLoenn && <TimeBankPanel id={u.id} />}
    </div>
  );
}
